import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { formatCEP, formatCPF, formatCNPJ } from '@brazilian-utils/brazilian-utils'

import {
  normalizeCreditCardNumber,
  normalizePhoneNumber,
  normalizeCreditCardExp,
} from '../../../services/mask'

import { PayOrder } from '../../OrderClose/PayOrder'
import { schema, FieldValues } from '../../../pages/Payment/validationSchema'
import { CustomerData } from '../../../interfaces/CustomerData'
import { useCart } from '../../../hooks/useCart'
import { estados } from '../../../pages/Payment/Options'
import { ContainerForm } from '../ContainerForm'
import { useEffect } from 'react'

export const FormPayment = () => {
  const { payOrder } = useCart()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FieldValues>({
    resolver: yupResolver(schema),
  })

  const cep = watch('cep', '')
  const typeDocument = watch('typeDocument', 'CPF')
  const document = watch('document', '')
  const mobile = watch('mobile', '')
  const creditNumber = watch('cardNumber', '')
  const creditCardExpiration = watch('cardExpiration', '')
  const code = watch('cardSecurityCode', '')

  const onSubmit: SubmitHandler<FieldValues> = (data) => payOrder(data as CustomerData)

  useEffect(() => {
    ;(async () => {
      if (cep) {
        setValue('cep', formatCEP(cep))
        const value = cep.replace('-', '')
        if (value.length === 8) {
          const res = await fetch(`https://viacep.com.br/ws/${value}/json/`)
          const json = await res.json()

          if (!json) return

          setValue('street', json.logradouro)
          setValue('complement', json.complento)
          setValue('neighborhood', json.bairro)
          setValue('city', json.localidade)
          setValue('state', json.uf)
        }
      }
    })()
  }, [cep])

  useEffect(() => {
    if (typeDocument === 'CPF') {
      setValue('document', formatCPF(document))
    } else if (typeDocument) {
      setValue('document', formatCNPJ(document))
    }
  }, [document])
  useEffect(() => setValue('mobile', normalizePhoneNumber(mobile)), [mobile])

  useEffect(() => {
    setValue('cardNumber', normalizeCreditCardNumber(creditNumber))
  }, [creditNumber])

  useEffect(() => {
    setValue('cardExpiration', normalizeCreditCardExp(creditCardExpiration))
  }, [creditCardExpiration])

  useEffect(() => {
    setValue('cardSecurityCode', code.replace(/\D/g, '').substring(0, 4))
  }, [code])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ContainerForm>
        <h4>Informações pessoais</h4>

        <div className={`field ${errors.fullName ? 'error' : ''}`}>
          <label htmlFor='fullName'>Nome e sobrenome</label>
          <input type='text' {...register('fullName')} />
          {errors.fullName && <p>{errors.fullName.message}</p>}
        </div>

        <div className='grouped'>
          <div className={`field ${errors.email ? 'error' : ''}`}>
            <label htmlFor='email'>E-mail</label>
            <input type='text' {...register('email')} />
            {errors.email && <p>{errors.email.message}</p>}
          </div>

          <div className={`field ${errors.mobile ? 'error' : ''}`}>
            <label htmlFor='mobile'>Celular</label>
            <input type='text' id='mobile' {...register('mobile')} />
            {errors.mobile && <p>{errors.mobile.message}</p>}
          </div>
        </div>

        <div className='grouped'>
          <div className='field'>
            <label htmlFor='doc'>CPF / CNPJ</label>
            <select id='' {...register('typeDocument')}>
              <option value='CPF'>CPF</option>
              <option value='CNPJ'>CNPJ</option>
            </select>
          </div>
          <div className={`field ${errors.document ? 'error' : ''}`}>
            <label htmlFor='document'>Seu {typeDocument}</label>
            <input type='text' {...register('document')} />
            {errors.document && <p>{errors.document.message}</p>}
          </div>
        </div>

        <h4>Endereço de entrega</h4>

        <div className={`field ${errors.cep ? 'error' : ''}`}>
          <label htmlFor='cep'>CEP</label>
          <input type='text' id='cep' {...register('cep')} style={{ width: 120 }} />
          {errors.cep && <p>{errors.cep.message}</p>}
        </div>

        <div className={`field ${errors.street ? 'error' : ''}`}>
          <label htmlFor='street'>Endereço</label>
          <input type='text' id='street' {...register('street')} />
          {errors.street && <p>{errors.street.message}</p>}
        </div>

        <div className='grouped'>
          <div className={`field ${errors.number ? 'error' : ''}`}>
            <label htmlFor='number'>Número</label>
            <input type='text' id='number' {...register('number')} />
            {errors.number && <p>{errors.number.message}</p>}
          </div>

          <div className={`field ${errors.complement ? 'error' : ''}`}>
            <label htmlFor='complement'>Complemento</label>
            <input type='text' id='complement' {...register('complement')} />
            {errors.complement && <p>{errors.complement.message}</p>}
          </div>
        </div>

        <div className='grouped'>
          <div className={`field ${errors.neighborhood ? 'error' : ''}`}>
            <label htmlFor='neighborhood'>Bairro</label>
            <input type='text' id='neighborhood' {...register('neighborhood')} />
            {errors.neighborhood && <p>{errors.neighborhood.message}</p>}
          </div>

          <div className={`field ${errors.city ? 'error' : ''}`}>
            <label htmlFor='city'>Cidade</label>
            <input type='text' id='city' {...register('city')} />
            {errors.city && <p>{errors.city.message}</p>}
          </div>

          <div className={`field ${errors.state ? 'error' : ''}`}>
            <label htmlFor='state'>Estado</label>

            <select id='state' {...register('state')}>
              {estados.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.text}
                </option>
              ))}
            </select>
            {errors.state && <p>{errors.state.message}</p>}
          </div>
        </div>

        <h4>Pagamento</h4>

        <div className={`field ${errors.cardNumber ? 'error' : ''}`}>
          <label htmlFor='cardNumber'>Número do cartão</label>
          <input type='text' id='cardNumber' {...register('cardNumber')} />
          {errors.cardNumber && <p>{errors.cardNumber.message}</p>}
        </div>

        <div className={`field ${errors.cardHolder ? 'error' : ''}`}>
          <label htmlFor='cardHolder'>Nome impresso no cartão</label>
          <input type='text' id='' {...register('cardHolder')} />
          {errors.cardHolder && <p>{errors.cardHolder.message}</p>}
        </div>

        <div className='grouped'>
          <div className={`field ${errors.cardExpiration ? 'error' : ''}`}>
            <label htmlFor='cardExpiration'>Validade (MM/AA)</label>
            <input type='text' id='' {...register('cardExpiration')} />
            {errors.cardExpiration && <p>{errors.cardExpiration.message}</p>}
          </div>

          <div className={`field ${errors.cardSecurityCode ? 'error' : ''}`}>
            <label htmlFor='cardSecurityCode'>Código de segurança (CVV)</label>
            <input type='text' {...register('cardSecurityCode')} />
            {errors.cardSecurityCode && <p>{errors.cardSecurityCode.message}</p>}
          </div>
        </div>
        <PayOrder />
      </ContainerForm>
    </form>
  )
}
